import type { Metadata } from "next";

import { buildLanguageAlternates, DEFAULT_LOCALE, LOCALE_TO_OG_LOCALE, SupportedLocale, withLocalePath } from "./locales";

export const LOCALIZED_SEO_PATHS = [
  "/",
  "/about",
  "/pricing",
  "/privacy",
  "/terms",
  "/features",
  "/support",
  "/video-to-text",
  "/transcribe-video-with-chatgpt",
  "/login",
  "/signup",
  "/payment/canceled",
  "/payment/success",
  "/tools",
  "/tools/chatgpt-transcription",
  "/tools/free-youtube-subtitles",
  "/tools/instagram-to-cooking-recipe",
  "/tools/instagram-to-transcript",
  "/tools/instagram-script-extractor",
  "/tools/instagram-transcript-from-link",
  "/tools/instagram-reel-transcript",
  "/tools/instagram-reel-hook-extractor",
  "/tools/instagram-reel-to-linkedin-post",
  "/tools/instagram-reel-to-blog-post",
  "/tools/tiktok-to-transcript",
  "/tools/tiktok-transcript-generator",
  "/tools/tiktok-video-hook-extractor",
  "/tools/tiktok-video-to-linkedin-post",
  "/tools/tiktok-video-to-blog-post",
  "/tools/video-transcript-generator",
  "/tools/video-to-text-converter",
  "/tools/mp3-to-lyrics",
  "/tools/mp4-to-audio",
  "/tools/podcast-transcription",
  "/tools/youtube-to-blog",
  "/tools/youtube-to-summary",
] as const;

const LOCALIZED_SEO_PREFIXES = ["/tools/"] as const;

export function isLocalizedSeoPath(path: string): boolean {
  return (
    LOCALIZED_SEO_PATHS.includes(path as (typeof LOCALIZED_SEO_PATHS)[number]) ||
    LOCALIZED_SEO_PREFIXES.some(prefix => path.startsWith(prefix))
  );
}

type LocalizedMetadataOptions = {
  locale: SupportedLocale;
  path: string;
  title: string;
  description?: string;
  image?: string;
};

export function buildLocalizedMetadata({ locale, path, title, description, image }: LocalizedMetadataOptions): Metadata {
  const url = withLocalePath(locale, path);
  const imageUrl = image ?? "https://videototextai.com/hero/video-to-text.webp";

  return {
    title,
    description,
    openGraph: {
      title,
      description,
      url,
      images: imageUrl
        ? [
            {
              url: imageUrl,
              width: 1200,
              height: 630,
              alt: title,
            },
          ]
        : undefined,
      type: "website",
      locale: LOCALE_TO_OG_LOCALE[locale],
    },
    alternates: {
      canonical: url,
      languages: {
        ...buildLanguageAlternates(path),
        "x-default": withLocalePath(DEFAULT_LOCALE, path),
      },
    },
  };
}
